import type { StepColumnType } from "@stepfinance/react";
import { formatBalance, StepAssetBreakdownPopoverChip, StepPopoverCellWrapper } from "@stepfinance/react";
import type { ReactNode } from "react";

import type { ReformattedYieldPosition } from "../../../../types/lending";
import type { ColumnMapType, ExtendedStepColumnsType, GetColumnsArgs } from "../../../../utils/columns";
import { getTokenBySymbol } from "../../../../utils/tokenInfo";
import { StepPopoverAssetWithIconRow, TokenIconText } from "../../../shared";
import CellValue from "../../../Table/CellValue";
import { TokenIcon } from "../../../TokenIcon";
import { AssetCell } from "./shared";

const vaultColumn = ({ commonText }: GetColumnsArgs["args"]): StepColumnType<ReformattedYieldPosition> => ({
  dataIndex: "vault",
  key: `lending-vault`,
  title: commonText.vault,
  className: "column-string",
  render(_, { vault, tokenSymbol }) {
    return (
      <AssetCell>
        <TokenIcon mintAddress={vault} />
        <TokenIconText bold>
          {tokenSymbol[0]}/{tokenSymbol[1]}
        </TokenIconText>
      </AssetCell>
    );
  },
});

const debtValueColumn = ({ lendingText }: GetColumnsArgs["args"]): StepColumnType<ReformattedYieldPosition> => ({
  dataIndex: "debtValue",
  key: `lending-debtValue`,
  title: lendingText.debtValue,
  className: "column-number",
  render(_, { debtTotalValueUSD, debtTokenAmountA, debtTokenAmountB, tokenSymbol }) {
    const contentRows: ReactNode[] = [];
    if (debtTokenAmountA > 0) {
      const symbol = tokenSymbol[0];
      const tokenInfo = getTokenBySymbol(symbol);
      const amount = formatBalance(debtTokenAmountA, tokenInfo?.decimals);

      contentRows.push(
        <StepPopoverAssetWithIconRow tokenIconMintAddress={tokenInfo?.address} amount={amount} symbol={symbol} />,
      );
    }

    if (debtTokenAmountB > 0) {
      const symbol = tokenSymbol[1];
      const tokenInfo = getTokenBySymbol(symbol);
      const amount = formatBalance(debtTokenAmountB, tokenInfo?.decimals);

      contentRows.push(
        <StepPopoverAssetWithIconRow tokenIconMintAddress={tokenInfo?.address} amount={amount} symbol={symbol} />,
      );
    }

    return (
      <StepPopoverCellWrapper>
        <StepAssetBreakdownPopoverChip
          title={lendingText.debtBreakdown}
          content={contentRows}
          assetValueDisplay={<CellValue cellType="value">{debtTotalValueUSD}</CellValue>}
          assetValue={debtTokenAmountA + debtTokenAmountB}
        />
      </StepPopoverCellWrapper>
    );
  },
  sorter: (a, b) => {
    return a.debtTotalValueUSD - b.debtTotalValueUSD;
  },
});

const equityUSDcolumn = ({ lendingText }: GetColumnsArgs["args"]): StepColumnType<ReformattedYieldPosition> => ({
  dataIndex: "equityUSD",
  key: `lending-equityUSD`,
  title: lendingText.equity,
  className: "column-number",
  render(_, { equityUSD }) {
    return <CellValue cellType="value">{equityUSD}</CellValue>;
  },
  sorter: (a, b) => {
    return a.equityUSD - b.equityUSD;
  },
});

const positionValueUSDColumn = ({
  lendingText,
  tableText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedYieldPosition> => ({
  dataIndex: "positionValueUSD",
  key: `lending-positionValueUSD`,
  title: lendingText.positionValue,
  className: "column-number",
  render(_, { positionAmountA, positionAmountB, positionValueUSD, tokenSymbol }) {
    const tokenSymbolA = tokenSymbol[0];
    const tokenSymbolB = tokenSymbol[1];
    const tokenAInfo = getTokenBySymbol(tokenSymbolA);
    const tokenBInfo = getTokenBySymbol(tokenSymbolB);
    const tokenBalanceADisplay = formatBalance(positionAmountA, tokenAInfo?.decimals);
    const tokenBalanceBDisplay = formatBalance(positionAmountB, tokenBInfo?.decimals);

    return (
      <StepPopoverCellWrapper>
        <StepAssetBreakdownPopoverChip
          assetValueDisplay={<CellValue cellType="value">{positionValueUSD}</CellValue>}
          assetValue={positionValueUSD}
          title={tableText.assetBreakdownPopover.title}
          content={[
            <StepPopoverAssetWithIconRow
              key={0}
              tokenIconMintAddress={tokenAInfo?.address}
              amount={tokenBalanceADisplay}
              symbol={tokenSymbolA}
            />,
            <StepPopoverAssetWithIconRow
              key={1}
              tokenIconMintAddress={tokenBInfo?.address}
              amount={tokenBalanceBDisplay}
              symbol={tokenSymbolB}
            />,
          ]}
        />
      </StepPopoverCellWrapper>
    );
  },
  sorter: (a, b) => {
    return a.positionValueUSD - b.positionValueUSD;
  },
  defaultSortOrder: "descend",
});

const defaultColumns: ExtendedStepColumnsType<ReformattedYieldPosition> = [
  vaultColumn,
  debtValueColumn,
  equityUSDcolumn,
  positionValueUSDColumn,
];

export const yieldColumnsMap: ColumnMapType<ReformattedYieldPosition> = {
  default: defaultColumns,
};
